/**
 * The page snapshot and the anchors of the page guide of Suge. pos-agent
 * `docs/chat-assistant-v1.md` (section 8) owns the format and the limits that this file repeats as
 * constants. The snapshot holds the structure of the page only: no field value and no table row.
 */

export type GuideTargetKind = 'field' | 'button' | 'tab';

export interface SnapshotElement {
  anchor: string;
  kind: GuideTargetKind;
  label: string;
  required?: boolean;
  disabled?: boolean;
  empty?: boolean;
  selected?: boolean;
  hidden?: boolean;
  error?: string;
}

export interface PageSnapshot {
  headings: string[];
  alerts: string[];
  elements: SnapshotElement[];
}

const MAX_HEADINGS = 20;
const MAX_ALERTS = 5;
const MAX_ELEMENTS = 150;
const HEADING_MAX_LENGTH = 120;
const ALERT_MAX_LENGTH = 300;
const LABEL_MAX_LENGTH = 120;
const ERROR_MAX_LENGTH = 200;
// The name part of an anchor. With the kind and a "#n" suffix, the anchor stays within 140.
const ANCHOR_NAME_MAX_LENGTH = 100;

// agent-api refuses control characters in the context fields (`\p{Cc}`, also U+0080 to U+009F).
const CONTROL_RE = /\p{Cc}+/gu;
const clean = (value: string | null | undefined, maxLength: number) =>
  (value ?? '').replace(CONTROL_RE, ' ').replace(/\s+/g, ' ').trim().slice(0, maxLength).trim();

/** True if the user can see the element. The tests give their own rule, because jsdom has no layout. */
export type VisibleRule = (element: Element) => boolean;

export const isVisibleOnScreen: VisibleRule = element => {
  const checkable = element as Element & { checkVisibility?: () => boolean };
  if (typeof checkable.checkVisibility === 'function') return checkable.checkVisibility();
  return element.getClientRects().length > 0;
};

// The guide never points into the chat panel or into its own bubble.
const IGNORED = '[data-suge-ignore]';
const FIELD = '[data-suge-field]';
const CONTROLS = `${FIELD}, .v-input, button, a.v-btn, [role="button"], [role="tab"]`;
// A list can show each record as a plain button with block content, for example a support
// ticket. Its text is record data, and the snapshot sends no record data. A Vuetify button is not
// a row, and a Vuetify tab holds a `div` for its slider.
const ROW_CONTENT = 'div, p, ul, ol, table';

/**
 * The parts of the page that the user can work with: an open dialog or right sheet, or else the
 * page content. The scrim of a dialog blocks the page behind it.
 */
export function snapshotRoots(doc: Document): Element[] {
  const dialogs = [...doc.querySelectorAll('.v-dialog.v-overlay--active > .v-overlay__content, .right-sheet-content')];
  if (dialogs.length > 0) return dialogs;
  const main = doc.querySelector('.v-main');
  return main ? [main] : [];
}

interface Candidate {
  element: Element;
  kind: GuideTargetKind;
  label: string;
  /** The name part of the anchor: a `data-suge` value or the label. */
  name: string;
}

const fieldLabel = (element: Element): string => {
  const own = element.getAttribute('data-suge-field');
  if (own !== null) return own;
  const label = element.querySelector('.v-field-label, .v-label');
  const input = element.querySelector('input, textarea');
  return label?.textContent ?? input?.getAttribute('aria-label') ?? input?.getAttribute('placeholder') ?? '';
};

const controlLabel = (element: Element): string =>
  element.getAttribute('aria-label') || element.textContent || element.getAttribute('title') || '';

const kindOf = (element: Element): GuideTargetKind | null => {
  if (element.matches(FIELD)) return 'field';
  // A Vuetify input inside a shared wrapper is a part of that field.
  if (element.closest(FIELD)) return null;
  if (element.matches('.v-input')) return 'field';
  if (element.matches('[role="tab"]')) return 'tab';
  // The buttons inside a field, for example the clear icon, belong to the field.
  if (element.closest('.v-input')) return null;
  return 'button';
};

function candidatesOf(roots: Element[]): Candidate[] {
  const found: Candidate[] = [];
  for (const root of roots) {
    for (const element of root.querySelectorAll(CONTROLS)) {
      if (element.closest(IGNORED)) continue;
      const kind = kindOf(element);
      if (kind === null) continue;
      if (kind !== 'field' && !element.matches('.v-btn') && element.querySelector(ROW_CONTENT)) continue;
      // A Vuetify tab is also a button, so it matches twice. The first match wins.
      if (found.some(candidate => candidate.element === element || candidate.element.contains(element))) continue;
      const label = clean(kind === 'field' ? fieldLabel(element) : controlLabel(element), LABEL_MAX_LENGTH);
      if (label === '') continue;
      const name = clean(element.getAttribute('data-suge') || label, ANCHOR_NAME_MAX_LENGTH);
      found.push({ element, kind, label, name });
    }
  }
  return found;
}

/** Gives each candidate its anchor. A repeated anchor gets "#2", "#3", in page order. */
function withAnchors(candidates: Candidate[]): (Candidate & { anchor: string })[] {
  const seen = new Map<string, number>();
  return candidates.map(candidate => {
    const base = `${candidate.kind}:${candidate.name}`;
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    return { ...candidate, anchor: count === 1 ? base : `${base}#${count}` };
  });
}

const isDisabled = (element: Element): boolean =>
  element.matches(':disabled, [aria-disabled="true"], .v-btn--disabled, .v-input--disabled') ||
  element.querySelector('.v-input--disabled, input:disabled, textarea:disabled') !== null;

function fieldState(element: Element): Pick<SnapshotElement, 'required' | 'empty' | 'error'> {
  const state: Pick<SnapshotElement, 'required' | 'empty' | 'error'> = {};
  if (element.hasAttribute('data-suge-required') || element.querySelector('[required], [aria-required="true"]')) {
    state.required = true;
  }
  // Vuetify marks a field with a value as dirty. A checkbox has no such field and no empty state.
  const field = element.querySelector('.v-field') ?? (element.matches('.v-field') ? element : null);
  if (field && !field.classList.contains('v-field--dirty')) state.empty = true;
  const error = element.matches('.v-input--error') || element.querySelector('.v-input--error') !== null;
  const message = error ? clean(element.querySelector('.v-messages__message')?.textContent, ERROR_MAX_LENGTH) : '';
  if (message !== '') state.error = message;
  return state;
}

function elementOf(candidate: Candidate & { anchor: string }, visible: VisibleRule): SnapshotElement {
  const { element, kind, label, anchor } = candidate;
  const item: SnapshotElement = { anchor, kind, label };
  if (kind === 'field') Object.assign(item, fieldState(element));
  if (isDisabled(element)) item.disabled = true;
  if (kind === 'tab' && (element.getAttribute('aria-selected') === 'true' || element.matches('.v-tab--selected'))) {
    item.selected = true;
  }
  if (!visible(element)) item.hidden = true;
  return item;
}

const textsOf = (roots: Element[], selector: string, visible: VisibleRule, max: number, maxLength: number) =>
  roots
    .flatMap(root => [...root.querySelectorAll(selector)])
    .filter(element => !element.closest(IGNORED) && visible(element))
    .map(element => clean(element.textContent, maxLength))
    .filter(text => text !== '')
    .slice(0, max);

/** The snapshot of the page that the user sees now, for the `context` of a chat run. */
export function pageSnapshot(doc: Document, visible: VisibleRule = isVisibleOnScreen): PageSnapshot {
  const roots = snapshotRoots(doc);
  const elements = withAnchors(candidatesOf(roots)).map(candidate => elementOf(candidate, visible));
  // Over the limit, the hidden elements go first, because the user cannot click them now.
  const shown =
    elements.length <= MAX_ELEMENTS
      ? elements
      : [...elements.filter(item => !item.hidden), ...elements.filter(item => item.hidden)]
          .slice(0, MAX_ELEMENTS)
          .sort((a, b) => elements.indexOf(a) - elements.indexOf(b));
  return {
    headings: textsOf(roots, 'h1, h2, h3, h4, .v-card-title', visible, MAX_HEADINGS, HEADING_MAX_LENGTH),
    alerts: textsOf(roots, '.v-alert', visible, MAX_ALERTS, ALERT_MAX_LENGTH),
    elements: shown,
  };
}

/** The element of an anchor on the page now, or null. It uses the rules of `pageSnapshot()`. */
export function findAnchor(doc: Document, anchor: string): Element | null {
  return withAnchors(candidatesOf(snapshotRoots(doc))).find(candidate => candidate.anchor === anchor)?.element ?? null;
}

/** The kind of an anchor, or null for a text that is not an anchor. */
export function anchorKind(anchor: string): GuideTargetKind | null {
  const kind = anchor.slice(0, anchor.indexOf(':'));
  return kind === 'field' || kind === 'button' || kind === 'tab' ? kind : null;
}
